import { useState } from 'react'
import { apiFetch } from '@/api'

/** Mirrors `AdminUserView` from the admin functions endpoint. */
interface AdminUserView {
  uid: string
  email: string | null
  emailVerified: boolean
  displayName: string | null
  disabled: boolean
  creationTime: string | null
  providers: string[]
}

/**
 * Root-only user admin actions: look up a Firebase Auth account by email and
 * override its email address. Used to rescue accounts that registered a typo'd
 * address (and are therefore stuck unverified — they never get the verify mail).
 */
const useUserAdmin = () => {
  const [loading, setLoading] = useState(false)

  const lookupUser = async (email: string): Promise<AdminUserView> => {
    setLoading(true)
    try {
      const res = await apiFetch(
        `/admin/user/lookup?email=${encodeURIComponent(email)}`
      )
      if (res.status === 404) {
        throw new Error('Fant ingen bruker med den e-postadressen.')
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => '')}`)
      }
      const body = (await res.json()) as { user: AdminUserView }
      return body.user
    } finally {
      setLoading(false)
    }
  }

  const changeEmail = async (uid: string, email: string): Promise<void> => {
    setLoading(true)
    try {
      const res = await apiFetch(`/admin/user/update/${uid}`, {
        method: 'POST',
        body: JSON.stringify({ email })
      })
      if (res.status === 409) {
        throw new Error('E-postadressen er allerede i bruk av en annen konto.')
      }
      if (res.status === 400) {
        throw new Error('Ugyldig e-postadresse.')
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => '')}`)
      }
    } finally {
      setLoading(false)
    }
  }

  return { lookupUser, changeEmail, loading }
}

export { useUserAdmin }
export type { AdminUserView }
